import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { MRT_Row, MRT_TableInstance, MRT_TableState } from '@talentmesh/table';
import { ApplicantRecruitmentOverviewResponse, CandidateTabs } from '@/Models/CandidateOverview';
import canSelectCandidate from '../../Utils/CanSelectCandidate';
import { CandidatesOverviewContextType } from '../CandidatesOverviewContext/CandidatesOverviewContext';
import useColumns from './utils/useColumns';
import useManualSorting from './utils/useManualSorting';
import { useShadcnTable } from '@/Hooks';

type CandidatesTableState = Partial<MRT_TableState<ApplicantRecruitmentOverviewResponse>>;
type MRTStateMap = Map<string, CandidatesTableState>;

export const CandidatesTableContext = createContext<
    MRT_TableInstance<ApplicantRecruitmentOverviewResponse> | undefined
>(undefined);

export const useCandidatesTable = ({
    assessmentId,
    candidates,
    totalCandidatesCount,
    loading: isLoading,
    tabValue,
    testTypes,
    includesExperienceCriteria,
    sort,
}: CandidatesOverviewContextType) => {
    const mrtStateKey = 'mrtState';
    const columns = useColumns(testTypes, includesExperienceCriteria);
    const { onSortingChange, sorting } = useManualSorting(sort);
    const enableRowSelection = useCallback(
        (row: MRT_Row<ApplicantRecruitmentOverviewResponse>) =>
            tabValue === CandidateTabs.Open && canSelectCandidate(row.original),
        [isLoading],
    );

    const initialState = useMemo<CandidatesTableState>(() => {
        const mrtStateStr = localStorage.getItem(mrtStateKey);
        const defaultState: Partial<CandidatesTableState> = {
            columnPinning: { left: ['mrt-row-select', 'isFavorite', 'applicant', 'applicantAction'] },
            columnVisibility: {
                phoneNumber: false,
                candidateEmail: false,
                noticePeriod: false,
                validWorkPermit: false,
                willingToRelocate: false,
                salaryExpectation: false,
                education: false,
                invitedAt: false,
                startedAt: false,
                invitedForInterviewAt: false,
                interviewingAt: false,
                hiredAt: false,
                rejectedAt: false,
            },
        };

        if (mrtStateStr) {
            const mrtStateMap: MRTStateMap = new Map(JSON.parse(mrtStateStr));
            return mrtStateMap.get(assessmentId) || defaultState;
        } else {
            return defaultState;
        }
    }, []);

    const table = useShadcnTable({
        columns,
        data: candidates,
        state: { showSkeletons: isLoading, isLoading: false, sorting },
        initialState,
        enableRowSelection,
        onSortingChange,
        rowCount: totalCandidatesCount,
        mantineSelectAllCheckboxProps: { disabled: tabValue !== CandidateTabs.Open },
    });

    useEffect(() => {
        return () => {
            let mrtStateMap: MRTStateMap = new Map();
            const mrtStateStr = localStorage.getItem(mrtStateKey);

            if (mrtStateStr) {
                mrtStateMap = new Map(JSON.parse(mrtStateStr));
            }

            // rowSelection only lives on the page, and shall not be persisted across
            const tableState: CandidatesTableState = { ...table.getState(), rowSelection: undefined };
            mrtStateMap.set(assessmentId, tableState);

            localStorage.setItem(mrtStateKey, JSON.stringify(Array.from(mrtStateMap.entries())));
        };
    }, []);

    return table;
};

export const useCandidatesTableContext = () => {
    const context = useContext(CandidatesTableContext);
    if (!context) {
        throw new Error('useCandidatesTableContext must be used within its provider');
    }
    return context;
};
